import React, { FC } from "react";
import { Patient } from "@models/patient";
import {
  Avatar,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import styled from "@emotion/styled";
import { ExpandMore } from "@mui/icons-material";

const Item = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
  margin-right: 4px;
  white-space: nowrap;
`;

const PICKER_WIDTH_PX = 200;

const ReadOnlyPatient = styled.div`
  display: flex;
  width: ${PICKER_WIDTH_PX}px;
  min-width: ${PICKER_WIDTH_PX}px;
  max-width: ${PICKER_WIDTH_PX}px;
  overflow: hidden;
`;

interface PatientSelectProps {
  patients: Patient[];
  selectedPatientId: string | null;
  onChange: (patientId: string | null) => void;
}

const getPatientName = (patient: Patient) => {
  if (!patient) {
    return "";
  }
  let name = `${patient.firstName || ""} ${patient.lastName || ""}`;
  if (!name) {
    name = patient.nickname;
  }

  return name;
};

const PatientSelect: FC<PatientSelectProps> = ({
  patients,
  selectedPatientId,
  onChange,
}) => {
  const onSelectPatient = (event: SelectChangeEvent) => {
    onChange((event.target.value as string) || null);
  };

  if (!patients?.length) {
    return null;
  }

  return (
    <div>
      {patients.length > 1 ? (
        <Select
          defaultValue=""
          size="small"
          value={selectedPatientId || undefined}
          onChange={onSelectPatient}
          sx={{
            width: PICKER_WIDTH_PX,
            minWidth: PICKER_WIDTH_PX,
            maxWidth: PICKER_WIDTH_PX,
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              pr: "28px",
            },
          }}
          variant="standard"
          disableUnderline={true}
          IconComponent={(props) => <ExpandMore {...props} />}
          renderValue={(value) => {
            const patient = patients.find((p) => p.id === value);
            if (!patient) {
              return null;
            }
            const name = getPatientName(patient);
            return (
              <Item>
                <Avatar
                  sx={{ width: 36, height: 36, flexShrink: 0 }}
                  alt={name}
                  src={patient.avatarUrl}
                />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}
                >
                  {name}
                </span>
              </Item>
            );
          }}
        >
          {patients.map((patient) => {
            const name = getPatientName(patient);
            return (
              <MenuItem key={patient.id} value={patient.id}>
                <Item>
                  <Avatar
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                    alt={name}
                    src={patient.avatarUrl}
                  />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                    }}
                  >
                    {name}
                  </span>
                </Item>
              </MenuItem>
            );
          })}
        </Select>
      ) : (
        <ReadOnlyPatient>
          <Item>
            <Avatar
              sx={{ width: 36, height: 36, flexShrink: 0 }}
              alt={getPatientName(patients[0])}
              src={patients[0].avatarUrl}
            />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {getPatientName(patients[0])}
            </span>
          </Item>
        </ReadOnlyPatient>
      )}
    </div>
  );
};

export default PatientSelect;
